const capitalCase = require('change-case').capitalCase;

/**
 * Utility functions related to head trauma checks.
 * @param {Object} headTraumaChecks - An object containing constants for head trauma checks.
 * @returns {Object} An object containing utility functions for head trauma checks.
 */
export function headTraumaUtils(headTraumaChecks) {
  /**
   * Cleans the head trauma checks based on the provided check value.
   * @param {string} checkValue - The check value to clean.
   * @returns {Array} An array containing the cleaned head trauma checks.
   */
  const cleanHeadTraumas = (checkValue) => {

    switch (checkValue) {
      case headTraumaChecks.NOT_DISCUSSED:
        return [{ label: capitalCase(headTraumaChecks.NOT_DISCUSSED), value: headTraumaChecks.NOT_DISCUSSED }];

      case headTraumaChecks.NONE_REPORTED:
        return [{ label: capitalCase(headTraumaChecks.NONE_REPORTED), value: headTraumaChecks.NONE_REPORTED }];

      case headTraumaChecks.DISCUSSED_IN_PREVIOUS_CALLS:
        return [{ label: capitalCase(headTraumaChecks.DISCUSSED_IN_PREVIOUS_CALLS), value: headTraumaChecks.DISCUSSED_IN_PREVIOUS_CALLS }];

      case headTraumaChecks.DISCUSSED_BUT_UNKNOWN:
        return [{ label: capitalCase(headTraumaChecks.DISCUSSED_BUT_UNKNOWN), value: headTraumaChecks.DISCUSSED_BUT_UNKNOWN }];

      case headTraumaChecks.REPORTED:
        return [];
    }
  };

  /**
   * Resolves the head trauma check from the stored head trauma value.
   * The API stores head trauma as a comma-separated string: a single sentinel
   * (e.g. "notDiscussed") for the non-"Reported" options, or the actual trauma
   * list (e.g. "swat,military") when "Reported".
   * @param {string} headTrauma - The stored comma-separated head trauma value.
   * @returns {string} The resolved head trauma check.
   */
  const resolveHeadTraumaCheck = (headTrauma) => {
    if (!headTrauma) return headTraumaChecks.NOT_DISCUSSED;

    const [firstHeadTrauma] = headTrauma.split(',');

    switch (firstHeadTrauma) {
      case headTraumaChecks.NOT_DISCUSSED:
      case headTraumaChecks.NONE_REPORTED:
      case headTraumaChecks.DISCUSSED_BUT_UNKNOWN:
      case headTraumaChecks.DISCUSSED_IN_PREVIOUS_CALLS:
        return firstHeadTrauma;
    }

    return headTraumaChecks.REPORTED;
  };

  return { cleanHeadTraumas, resolveHeadTraumaCheck };
}

/**
 * Utility functions for form validation and payload formatting.
 * @param {Object} lookups - The lookup object containing various options for form fields.
 * @returns {Object} An object containing the utility functions.
 */
export function formUtils(lookups) {

  /**
   * Checks the fields of the payload for validation errors.
   * @param {Object} payload - The payload object containing form data.
   * @returns {Object} An object containing the validation errors, if any.
   */
  const checkFields = (payload) => {
    let validationErrors = "";
    if (!payload.name) {
      validationErrors += "Please enter a Caller Name. ";
    }
    if (!payload.callReason && !payload.originCallReportId) {
      validationErrors += "Please enter the Summary Narrative. ";
    }
    if (payload.originCallReportId && !payload.returnCallFollowUpNotes) {
      validationErrors += "Please add Follow Up Notes. ";
    }
    if (
      payload.headTraumaCheck === lookups.headTraumaChecks.REPORTED &&
      !payload.headTrauma.length
    ) {
      validationErrors += "Please select Head/Neck Impact and Trauma (HIT) reported. ";
    }
    if (!payload.currentStruggle) {
      validationErrors += "Please select a Caller Presenting Issue value. ";
    }
    if (!payload.coplineReferral) {
      validationErrors += "Plase select a Heard About Copline value. ";
    }
    if (payload.isSuicidalCall === undefined || payload.isSuicidalCall === null) {
      validationErrors += "Please indicate whether the call was suicidal. ";
    }
    if (!payload.cellPhone) {
      validationErrors += "Please enter a Caller Area Code. ";
    }

    return { validationErrors };    
  };

  /**
   * Formats the payload object before sending it to the server.
   * @param {Object} payload - The payload object containing form data.
   * @returns {Object} The formatted payload object.
   */
  const formatPayload = (payload) => {
    for (let [key, value] of Object.entries(payload)) {
      if (
        [
          'veteranBranch',
          'veteranRank',
          'provinceState',
          'country',
          'callerType',
          'returnCallTimeZone',
          'coplineReferral',
          'impairment',
          'currentStruggle',
          'returnCallTimezone',
          'callEntryType',
        ].includes(key)
      ) {
        // for select form items, send only value to server, not {label, value}
        payload[key] = value ? value["value"] : '';
      } else if (['headTrauma'].includes(key)) {
        // same for head_trauma form field, send an array of the selected values
        payload[key] = payload[key].map((formObject) => formObject.value);
      }
    }
    return payload;
  };

  return { checkFields, formatPayload }

}
